import type { NormalizedCacheObject } from "@apollo/client";
import { createApolloClient, getApolloClient } from "@appcaire/graphql";
import { getGraphqlUrl, getGraphqlWsUrl } from "./env";

function defaultGraphqlUrl(): string {
  return typeof window === "undefined"
    ? "http://localhost:4005/graphql"
    : "/api/graphql";
}

function defaultGraphqlWsUrl(): string {
  if (typeof window === "undefined") return "ws://localhost:4005/graphql";
  return `${window.location.origin.replace(/^http/, "ws")}/api/graphql`;
}

let browserClient: ReturnType<typeof getApolloClient> | null = null;

/**
 * Create one Apollo cache for one server render. Never replace this with the
 * browser singleton: sharing an SSR cache can leak rendered data across users.
 */
export function createSsrApolloClient(
  graphqlUrl: string,
  abortSignal?: AbortSignal,
) {
  if (!/^https?:\/\//i.test(graphqlUrl)) {
    throw new Error("SSR GraphQL URL must be absolute");
  }

  let operationError: Error | null = null;
  const client = createApolloClient({
    graphqlUrl,
    connectToDevTools: false,
    ssrMode: true,
    abortSignal,
    onOperationError: (error) => {
      operationError ??= error;
    },
    getToken: async () => null,
  });

  return { client, getOperationError: () => operationError };
}

export function getBrowserApolloClient() {
  if (!browserClient) {
    browserClient = getApolloClient({
      graphqlUrl: getGraphqlUrl() || defaultGraphqlUrl(),
      graphqlWsUrl: getGraphqlWsUrl() || defaultGraphqlWsUrl(),
      connectToDevTools: import.meta.env.DEV,
      ssrForceFetchDelay: 100,
      getToken: async () => null,
    });
  }
  return browserClient;
}

export function restoreApolloState(
  initialState: NormalizedCacheObject | undefined,
): void {
  if (initialState) getBrowserApolloClient().cache.restore(initialState);
}
